import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  AcademicCapIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useCourses } from '../../contexts/CourseContext';
import { useApplications } from '../../contexts/ApplicationContext';
import { testStudents } from '../../data/testStudents';
import { getActualCourseStatus, getKSTDateString } from '../../utils/dateUtils';
import { supabase } from '../../lib/supabase';

const Dashboard: React.FC = () => {
  const { courses } = useCourses();
  const { applications, getApplicationsByCourse } = useApplications();

  // 통계 데이터 계산
  const stats = useMemo(() => {
    const totalCourses = courses.length;
    const activeCourses = courses.filter(c => {
      const actualStatus = getActualCourseStatus(c);
      return actualStatus === 'ongoing' || actualStatus === 'recruiting';
    }).length;

    // 유니크한 학생 수 (신청 중 상태)
    const uniqueStudents = new Set(
      applications
        .filter(app => app.status !== 'cancelled')
        .map(app => app.student_id)
    );
    const totalStudents = uniqueStudents.size;

    // 오늘 신청 수 (KST 기준)
    const today = getKSTDateString();
    const todayApplications = applications.filter(app =>
      app.created_at?.startsWith(today)
    ).length;

    // 월 수익 (등록 완료된 신청의 수강료 합계)
    // 월간 수익 계산은 Statistics 페이지에서 확인 (실제 결제 금액 기반)
    // 대시보드에서는 간략 표시용으로 0으로 설정
    const monthlyRevenue = 0;

    // 수료율 (임시로 85%로 설정, 실제로는 완료된 강좌의 수료 데이터 필요)
    const completionRate = 85;

    return {
      totalCourses,
      activeCourses,
      totalStudents,
      todayApplications,
      monthlyRevenue,
      completionRate
    };
  }, [courses, applications]);

  // 유입경로 통계
  const [referralStats, setReferralStats] = useState<{ source: string; count: number }[]>([]);
  const [referralLoading, setReferralLoading] = useState(true);

  useEffect(() => {
    const loadReferralStats = async () => {
      setReferralLoading(true);
      try {
        const { data } = await supabase
          .from('profiles')
          .select('referral_source')
          .not('referral_source', 'is', null)
          .neq('user_type', 'admin');

        if (data && data.length > 0) {
          const counts: Record<string, number> = {};
          data.forEach((row: any) => {
            let source = row.referral_source;
            if (source.startsWith('기타:')) {
              source = '기타';
            } else if (source.includes(' | ')) {
              source = source.split(' | ')[0];
            }
            counts[source] = (counts[source] || 0) + 1;
          });
          const sorted = Object.entries(counts)
            .map(([source, count]) => ({ source, count }))
            .sort((a, b) => b.count - a.count);
          setReferralStats(sorted);
        }
      } catch (error) {
        console.error('Failed to load referral stats:', error);
      } finally {
        setReferralLoading(false);
      }
    };
    loadReferralStats();
  }, []);

  // 최근 신청 (최신 5개)
  const recentApplications = useMemo(() => {
    return [...applications]
      .sort((a, b) => {
        const dateA = new Date(a.created_at || '').getTime();
        const dateB = new Date(b.created_at || '').getTime();
        return dateB - dateA;
      })
      .slice(0, 5)
      .map(app => {
        const course = courses.find(c => c.id === app.program_id);
        const student = testStudents.find(s => s.id === app.student_id);
        const appDate = new Date(app.created_at || '');
        const now = new Date();
        const diffMs = now.getTime() - appDate.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        let timeAgo = '';
        if (diffDays > 0) timeAgo = `${diffDays}일 전`;
        else if (diffHours > 0) timeAgo = `${diffHours}시간 전`;
        else if (diffMins > 0) timeAgo = `${diffMins}분 전`;
        else timeAgo = '방금 전';

        return {
          id: app.application_id,
          student: student?.name || app.student_name || '알 수 없음',
          course: course?.courseName || app.program_name || '알 수 없음',
          time: timeAgo,
          status: app.status
        };
      });
  }, [applications, courses]);

  // 개강 예정 강좌 (시작일이 미래인 강좌, 최대 4개)
  const upcomingCourses = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];

    return courses
      .filter(course => course.courseStartDate && course.courseStartDate >= today)
      .sort((a, b) => (a.courseStartDate || '').localeCompare(b.courseStartDate || ''))
      .slice(0, 4)
      .map(course => {
        const courseApplications = getApplicationsByCourse(course.id);
        const applicants = courseApplications.filter(app =>
          app.status !== 'cancelled'
        ).length;

        return {
          id: course.id,
          name: course.courseName,
          startDate: course.courseStartDate || '',
          applicants,
          capacity: course.maxStudents
        };
      });
  }, [courses, getApplicationsByCourse]);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">대시보드</h1>
        <p className="text-gray-600">전체 운영 현황을 한눈에 확인하세요</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">전체 강좌</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.totalCourses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CalendarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">진행중</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.activeCourses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <UsersIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">총 수강생</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.totalStudents}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">오늘 신청</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.todayApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">월 수익</p>
              <p className="text-2xl font-semibold text-gray-800">
                {(stats.monthlyRevenue / 1000000).toFixed(1)}M
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">수료율</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.completionRate}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Applications */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">최근 수강 신청</h2>
          </div>
          <div className="p-6">
            {recentApplications.length === 0 ? (
              <p className="text-gray-500 text-center py-8">최근 신청 내역이 없습니다.</p>
            ) : (
              <div className="space-y-4">
                {recentApplications.map((application) => (
                <div key={application.id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{application.student}</p>
                    <p className="text-sm text-gray-500">{application.course}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-xs text-gray-500">{application.time}</span>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        application.status === 'registered'
                          ? 'bg-green-100 text-green-800'
                          : application.status === 'cancelled'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {application.status === 'registered'
                        ? '접수완료'
                        : application.status === 'cancelled'
                        ? '취소'
                        : '대기'}
                    </span>
                  </div>
                </div>
                ))}
              </div>
            )}
            {recentApplications.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link
                  to="/admin/students"
                  className="text-sm text-green-600 hover:text-green-800 font-medium"
                >
                  전체 신청 목록 보기 →
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Courses */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">개강 예정 강좌</h2>
          </div>
          <div className="p-6">
            {upcomingCourses.length === 0 ? (
              <p className="text-gray-500 text-center py-8">개강 예정 강좌가 없습니다.</p>
            ) : (
              <div className="space-y-4">
                {upcomingCourses.map((course) => (
                <div key={course.id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{course.name}</p>
                    <p className="text-sm text-gray-500">시작일: {course.startDate}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {course.applicants}/{course.capacity}명
                    </p>
                    <div className="mt-1 w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          course.applicants >= course.capacity
                            ? 'bg-red-600'
                            : course.applicants >= course.capacity * 0.8
                            ? 'bg-yellow-600'
                            : 'bg-green-600'
                        }`}
                        style={{
                          width: `${Math.min(
                            (course.applicants / course.capacity) * 100,
                            100
                          )}%`
                        }}
                      />
                    </div>
                  </div>
                </div>
                ))}
              </div>
            )}
            {upcomingCourses.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link
                  to="/admin/courses"
                  className="text-sm text-green-600 hover:text-green-800 font-medium"
                >
                  전체 강좌 관리 →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 유입경로 통계 */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">유입경로 통계</h2>
          <p className="text-sm text-gray-500 mt-1">회원가입 시 선택한 교육센터 인지 경로</p>
        </div>
        <div className="p-6">
          {referralLoading ? (
            <div className="text-center py-8">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-green-600 border-r-transparent"></div>
              <p className="mt-2 text-sm text-gray-500">통계 로딩 중...</p>
            </div>
          ) : referralStats.length === 0 ? (
            <p className="text-gray-500 text-center py-8">아직 유입경로 데이터가 없습니다.</p>
          ) : (
            <div className="space-y-3">
              {referralStats.map((item) => {
                const maxCount = referralStats[0].count;
                const percentage = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                const total = referralStats.reduce((sum, s) => sum + s.count, 0);
                const ratio = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0';
                return (
                  <div key={item.source} className="flex items-center gap-4">
                    <div className="w-52 flex-shrink-0 text-sm text-gray-700 truncate" title={item.source}>
                      {item.source}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                      <div
                        className="h-full bg-[#48c9a6] rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-20 flex-shrink-0 text-right text-sm font-medium text-gray-700">
                      {item.count}명 ({ratio}%)
                    </div>
                  </div>
                );
              })}
              <div className="pt-3 border-t border-gray-200 text-right text-sm text-gray-500">
                총 {referralStats.reduce((sum, s) => sum + s.count, 0)}명 응답
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;